import {t, tpl} from '../../i18n/i18n.js';
import {
    FOCAL_POINT_VALUES,
    FieldType,
    GRID_MAX_COLUMNS,
    GridKey,
    PageKey,
    UPLOAD_IMAGE_TYPES,
    altKey,
    isTextFieldType,
    positionKey,
} from '../../core/vocabulary.js';
import {isPreparableImageType, uploadProblem} from '../../core/page/upload.core.js';
import {createSlugMoveWarning, createSlugPreviewPill} from './slug-preview.js';
import {prepareImage} from './image-prep.js';
import {clearExistingAsset} from './existing-asset.js';

const DEFAULT_FOCAL_POINT = 'center center';

/**
 * Photos already prepared for upload; their `change` passes straight through.
 * @type {WeakSet<File>}
 */
const preparedFiles = new WeakSet();

/**
 * Photo preparations still running, in any form or item editor.
 * @type {Set<Promise<void>>}
 */
const preparations = new Set();

/**
 * Waits for every photo still being prepared, so a save sends the prepared files rather than
 * the originals. Resolves once each prepared file has replaced its original and its `change`
 * has been reported; null when no photo is being prepared.
 * @returns {Promise<void> | null}
 */
export function imagePreparationsSettled() {
    return preparations.size > 0 ? Promise.all(preparations).then(() => undefined) : null;
}

/**
 * Whether an image control holds a JPEG or PNG the browser has not prepared yet (see
 * {@link prepareImage}). Its `change` is held back and dispatched again once the prepared file
 * has replaced it.
 * @param {HTMLElement} control
 * @returns {boolean}
 */
export function awaitsImagePreparation(control) {
    const file = control.dataset.fieldType === FieldType.IMAGE ? control.files[0] : undefined;
    return Boolean(file) && isPreparableImageType(file.type) && !preparedFiles.has(file);
}

/** Translation key of the spoken name of each focal point. */
const FOCAL_POINT_LABEL_KEYS = Object.freeze({
    'left top': 'pageForm.focalLeftTop',
    'center top': 'pageForm.focalCenterTop',
    'right top': 'pageForm.focalRightTop',
    'left center': 'pageForm.focalLeftCenter',
    'center center': 'pageForm.focalCenterCenter',
    'right center': 'pageForm.focalRightCenter',
    'left bottom': 'pageForm.focalLeftBottom',
    'center bottom': 'pageForm.focalCenterBottom',
    'right bottom': 'pageForm.focalRightBottom',
});

/**
 * Marks the chosen dot of a focal-point grid, visibly and as the pressed button.
 * @param {HTMLElement} grid a `.focal-point-grid`
 * @param {string} value
 */
export function markFocalPoint(grid, value) {
    grid.querySelectorAll('.focal-point-dot').forEach((dot) => {
        const chosen = dot.dataset.value === value;
        dot.classList.toggle('is-active', chosen);
        dot.setAttribute('aria-pressed', String(chosen));
    });
}

/**
 * @typedef {Object} FieldRenderContext
 * @property {string[]} fieldKeys Every field key of the template; an image only generates the
 *   alt-text and focal-point controls its template does not declare itself
 * @property {(fieldKey: string) => import('./layout-placement.js').GridFieldHint | null} [gridHint] Grid
 *   placement hint of a number field; without it no field gets one
 * @property {(input: HTMLInputElement, fieldKey: string) => void} [bindLayoutToggle] Binds a checkbox
 *   that toggles a layout context; without it checkboxes toggle nothing
 * @property {() => void} onFocalPointChange
 * @property {boolean} [allowRemove] Image and document fields get a remove action that clears
 *   their stored or pending value
 * @property {boolean} [plainSlug] A field keyed `slug` renders as plain text, without the slug
 *   hint and URL preview pill
 */

/** @returns {HTMLSpanElement} the " (required)" marker appended to a field label */
function requiredIndicator() {
    const req = document.createElement('span');
    req.className = 'required-indicator';
    req.setAttribute('aria-hidden', 'true');
    req.textContent = ' ' + t('pageForm.required');
    return req;
}

/**
 * Adds or removes the required marker that an editor with layout-dependent rules toggles on a
 * field label (`.section-required-indicator`), leaving at most one.
 * @param {HTMLElement} label
 * @param {boolean} required
 */
export function syncRequiredIndicator(label, required) {
    const indicator = label.querySelector('.section-required-indicator');
    if (required && !indicator) {
        const element = requiredIndicator();
        element.classList.add('section-required-indicator');
        label.appendChild(element);
    } else if (!required && indicator) {
        indicator.remove();
    }
}

/**
 * Adds a remove action after the upload drop zone, shown while the field holds a stored or
 * pending file. Removing clears both, reports a bubbling `change` on the file input and moves
 * focus to that input, because the action hides itself.
 */
function appendRemoveAction(field, input, wrapper) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'current-asset-link asset-remove';
    button.setAttribute('aria-label', tpl('pageForm.removeAssetLabel', {label: field.label}));
    const icon = document.createElement('i');
    icon.className = 'fa-solid fa-trash-can';
    icon.setAttribute('aria-hidden', 'true');
    button.appendChild(icon);
    button.appendChild(document.createTextNode(` ${t('pageForm.removeAsset')}`));
    const sync = () => {
        button.hidden = input.files.length === 0 && !input.dataset.existingPath;
    };
    input.addEventListener('change', sync);
    button.addEventListener('click', () => {
        clearExistingAsset(input);
        input.value = '';
        input.dispatchEvent(new Event('change', {bubbles: true}));
        input.focus();
    });
    wrapper.insertAdjacentElement('afterend', button);
    sync();
}

function renderTextarea(field, group) {
    const input = document.createElement('textarea');
    input.rows = 4;
    input.placeholder = tpl('pageForm.inputPlaceholder', {label: field.label.toLowerCase()});
    group.appendChild(input);
    return input;
}

/**
 * Prepares the photo an image input holds and puts it in the input's place, then reports the
 * input's `change` again. A result that arrives after the user chose another file, or removed
 * it, is dropped.
 * @param {HTMLInputElement} input
 * @param {boolean} bubbles whether the reported `change` bubbles, like the one it repeats
 */
async function reportPrepared(input, bubbles) {
    const file = input.files[0];
    const prepared = await prepareImage(file);
    if (input.files[0] !== file) return;
    const transfer = new DataTransfer();
    transfer.items.add(prepared);
    input.files = transfer.files;
    preparedFiles.add(input.files[0]);
    input.dispatchEvent(new Event('change', {bubbles}));
}

/**
 * Checks every file chosen for an upload field against the backend's limits. A refused file
 * is cleared from the input straight away and a message under the drop zone says why, so the
 * problem surfaces now instead of when the form is saved. Registered before the field's other
 * `change` listeners, so they only ever see accepted files.
 *
 * A JPEG or PNG on an image field is first prepared in the browser ({@link prepareImage}): its
 * `change`, and any `input` while it waits, stop here, and the `change` is dispatched again
 * with the prepared file, so the later listeners only ever see prepared files.
 * @param {HTMLInputElement} input
 * @param {string} fieldKey
 * @param {boolean} image
 * @returns {HTMLParagraphElement} the message element, to place after the drop zone
 */
function guardUploads(input, fieldKey, image) {
    const error = document.createElement('p');
    error.className = 'upload-error';
    error.id = `upload-error-${fieldKey}`;
    error.setAttribute('role', 'alert');
    error.hidden = true;
    input.addEventListener('change', (event) => {
        if (awaitsImagePreparation(input)) {
            event.stopImmediatePropagation();
            // `reportPrepared` never rejects: a photo the browser cannot prepare stays as it is.
            const preparation = reportPrepared(input, event.bubbles);
            preparations.add(preparation);
            preparation.then(() => preparations.delete(preparation));
            return;
        }
        const problem = [...input.files].map((file) => uploadProblem(file, {image})).find(Boolean);
        error.hidden = !problem;
        error.textContent = problem ? tpl(problem.key, problem.vars) : '';
        const ids = (input.getAttribute('aria-describedby') || '').split(' ').filter((id) => id && id !== error.id);
        if (problem) {
            input.value = '';
            ids.push(error.id);
        }
        if (ids.length > 0) {
            input.setAttribute('aria-describedby', ids.join(' '));
        } else {
            input.removeAttribute('aria-describedby');
        }
    });
    if (image) {
        input.addEventListener('input', (event) => {
            if (awaitsImagePreparation(input)) event.stopImmediatePropagation();
        });
    }
    return error;
}

/**
 * Drag and drop onto an upload drop zone. While a file is dragged over it, the zone shows the
 * drop text; afterwards it shows its own text again. An image field keeps the dropped images
 * when there are any; otherwise the dropped files go to the input as they are, so its upload
 * check can say why they do not fit. The input then reports a bubbling `change`, like a file
 * chosen through the dialog, so the form hears the drop.
 * @param {HTMLLabelElement} dropLabel
 * @param {HTMLElement} uploadText
 * @param {HTMLInputElement} input
 * @param {boolean} image
 */
function bindDropZone(dropLabel, uploadText, input, image) {
    let idleText = uploadText.textContent;
    const reset = () => {
        dropLabel.classList.remove('drag-active');
        uploadText.textContent = idleText;
    };
    dropLabel.addEventListener('dragover', (e) => {
        e.preventDefault();
        if (!dropLabel.classList.contains('drag-active')) idleText = uploadText.textContent;
        dropLabel.classList.add('drag-active');
        uploadText.textContent = t('pageForm.uploadDrop');
    });
    dropLabel.addEventListener('dragleave', (e) => {
        if (!dropLabel.contains(e.relatedTarget)) reset();
    });
    dropLabel.addEventListener('drop', (e) => {
        e.preventDefault();
        reset();
        const files = [...(e.dataTransfer?.files ?? [])];
        if (files.length === 0) return;
        const images = files.filter((file) => file.type.startsWith('image/'));
        const dt = new DataTransfer();
        for (const file of image && images.length > 0 ? images : files) dt.items.add(file);
        input.files = dt.files;
        input.dispatchEvent(new Event('change', {bubbles: true}));
    });
}

/** The accepted formats and size limit, shown inside a drop zone. */
function uploadLimits(key) {
    const limits = document.createElement('span');
    limits.className = 'file-upload-limits';
    limits.textContent = t(key);
    return limits;
}

/**
 * Collapsible upload card with drag and drop, thumbnails, and (unless the template declares
 * them) an alt-text input and a focal-point picker.
 */
function renderImage(field, group, {fieldKeys, onFocalPointChange, allowRemove}) {
    group.className = 'field-group field-group--image';

    // Optional image sections start collapsed so long forms don't drown the
    // user. Required fields and edit-mode sections with existing content
    // get force-expanded by the page form's prefillData.
    const startExpanded = !!field.required;
    const bodyId = `image-body-${field.key}`;

    const cardHeader = document.createElement('button');
    cardHeader.type = 'button';
    cardHeader.className = 'image-card-header';
    cardHeader.setAttribute('aria-expanded', String(startExpanded));
    cardHeader.setAttribute('aria-controls', bodyId);

    const headerLabel = document.createElement('span');
    headerLabel.className = 'image-card-label';
    headerLabel.textContent = field.label;
    if (field.required) headerLabel.appendChild(requiredIndicator());
    cardHeader.appendChild(headerLabel);

    const chevron = document.createElement('i');
    chevron.className = 'fa-solid fa-chevron-down image-card-chevron';
    chevron.setAttribute('aria-hidden', 'true');
    cardHeader.appendChild(chevron);

    cardHeader.addEventListener('click', () => {
        const expanded = cardHeader.getAttribute('aria-expanded') === 'true';
        cardHeader.setAttribute('aria-expanded', String(!expanded));
    });

    group.appendChild(cardHeader);

    const cardBody = document.createElement('div');
    cardBody.id = bodyId;
    cardBody.className = 'image-card-body';

    const wrapper = document.createElement('div');
    wrapper.className = 'file-upload-wrapper';

    const input = document.createElement('input');
    input.type = 'file';
    input.className = 'file-input-hidden';
    input.accept = UPLOAD_IMAGE_TYPES.join(',');
    input.dataset.fieldType = FieldType.IMAGE;
    const uploadError = guardUploads(input, field.key, true);

    const dropLabel = document.createElement('label');
    dropLabel.className = 'file-upload-label';
    dropLabel.setAttribute('for', field.key);
    const iconEl = document.createElement('i');
    iconEl.className = 'fa-solid fa-cloud-arrow-up';
    iconEl.setAttribute('aria-hidden', 'true');
    dropLabel.appendChild(iconEl);

    const uploadText = document.createElement('span');
    uploadText.className = 'file-upload-text';
    uploadText.textContent = t('pageForm.uploadClick');
    dropLabel.appendChild(uploadText);

    const fileName = document.createElement('span');
    fileName.className = 'file-upload-name';
    fileName.id = `name-${field.key}`;
    fileName.textContent = t('pageForm.noFileSelected');
    dropLabel.appendChild(fileName);
    dropLabel.appendChild(uploadLimits('pageForm.uploadImageLimits'));

    const previews = document.createElement('div');
    previews.className = 'image-previews';

    const updatePreview = () => {
        const nameEl = dropLabel.querySelector('.file-upload-name');
        nameEl.textContent = input.files.length > 0 ? input.files[0].name : t('pageForm.noFileSelected');

        for (const img of previews.querySelectorAll('img')) {
            URL.revokeObjectURL(img.src);
        }
        while (previews.firstChild) previews.removeChild(previews.firstChild);
        for (const file of input.files) {
            const img = document.createElement('img');
            img.src = URL.createObjectURL(file);
            img.alt = file.name;
            previews.appendChild(img);
        }
    };

    input.addEventListener('change', updatePreview);
    bindDropZone(dropLabel, uploadText, input, true);

    wrapper.appendChild(input);
    wrapper.appendChild(dropLabel);
    cardBody.appendChild(wrapper);
    cardBody.appendChild(uploadError);
    cardBody.appendChild(previews);
    if (allowRemove) appendRemoveAction(field, input, wrapper);

    const hasExplicitAltField = fieldKeys.includes(altKey(field.key));
    if (!hasExplicitAltField) {
        const altSubLabel = document.createElement('label');
        altSubLabel.setAttribute('for', altKey(field.key));
        altSubLabel.className = 'sub-label';
        altSubLabel.textContent = t('pageForm.altTextLabel');
        cardBody.appendChild(altSubLabel);
        const altInput = document.createElement('textarea');
        altInput.rows = 3;
        altInput.name = altKey(field.key);
        altInput.id = altKey(field.key);
        altInput.placeholder = tpl('pageForm.imageAltPlaceholder', {label: field.label.toLowerCase()});
        cardBody.appendChild(altInput);
        const altHint = document.createElement('small');
        altHint.className = 'field-hint';
        altHint.textContent = t('pageForm.altTextHint');
        cardBody.appendChild(altHint);
    }

    const imagePositionKey = positionKey(field.key);
    const hasExplicitPositionField = fieldKeys.includes(imagePositionKey);
    if (!hasExplicitPositionField) {
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.name = imagePositionKey;
        hiddenInput.value = DEFAULT_FOCAL_POINT;

        const pickerLabel = document.createElement('span');
        pickerLabel.className = 'focal-point-label';
        pickerLabel.textContent = t('pageForm.focalPoint');

        const grid = document.createElement('div');
        grid.className = 'focal-point-grid';
        grid.dataset.for = imagePositionKey;
        grid.setAttribute('role', 'group');
        grid.setAttribute('aria-label', t('pageForm.focalPoint'));

        for (const value of FOCAL_POINT_VALUES) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'focal-point-dot';
            btn.dataset.value = value;
            btn.setAttribute('aria-label', t(FOCAL_POINT_LABEL_KEYS[value]));

            btn.addEventListener('click', () => {
                hiddenInput.value = value;
                markFocalPoint(grid, value);
                const previewImg = previews.querySelector('img') ?? group.querySelector('.current-image img');
                if (previewImg) previewImg.style.objectPosition = value;
                onFocalPointChange();
            });
            grid.appendChild(btn);
        }
        markFocalPoint(grid, DEFAULT_FOCAL_POINT);

        const pickerWrapper = document.createElement('div');
        pickerWrapper.className = 'focal-point-picker';
        pickerWrapper.appendChild(pickerLabel);
        pickerWrapper.appendChild(grid);
        pickerWrapper.appendChild(hiddenInput);
        cardBody.appendChild(pickerWrapper);
        const focalHint = document.createElement('small');
        focalHint.className = 'field-hint';
        focalHint.textContent = t('pageForm.focalPointHint');
        cardBody.appendChild(focalHint);
    }
    group.appendChild(cardBody);
    return input;
}

function renderDocument(field, group, {allowRemove}) {
    const wrapper = document.createElement('div');
    wrapper.className = 'file-upload-wrapper';

    const input = document.createElement('input');
    input.type = 'file';
    input.className = 'file-input-hidden';
    input.dataset.fieldType = FieldType.DOCUMENT;
    const uploadError = guardUploads(input, field.key, false);

    const dropLabel = document.createElement('label');
    dropLabel.className = 'file-upload-label';
    dropLabel.setAttribute('for', field.key);
    const iconEl = document.createElement('i');
    iconEl.className = 'fa-solid fa-cloud-arrow-up';
    iconEl.setAttribute('aria-hidden', 'true');
    dropLabel.appendChild(iconEl);

    const uploadText = document.createElement('span');
    uploadText.className = 'file-upload-text';
    uploadText.textContent = t('pageForm.uploadDocumentClick');
    dropLabel.appendChild(uploadText);

    const fileName = document.createElement('span');
    fileName.className = 'file-upload-name';
    fileName.id = `name-${field.key}`;
    fileName.textContent = t('pageForm.noFileSelected');
    dropLabel.appendChild(fileName);
    dropLabel.appendChild(uploadLimits('pageForm.uploadDocumentLimits'));
    bindDropZone(dropLabel, uploadText, input, false);

    input.addEventListener('change', () => {
        const nameEl = dropLabel.querySelector('.file-upload-name');
        if (input.files.length > 0) {
            nameEl.textContent = input.files[0].name;
        } else {
            nameEl.textContent = t('pageForm.noFileSelected');
        }
    });

    wrapper.appendChild(input);
    wrapper.appendChild(dropLabel);
    group.appendChild(wrapper);
    group.appendChild(uploadError);
    if (allowRemove) appendRemoveAction(field, input, wrapper);
    return input;
}

function renderTable(field, group) {
    group.className = 'field-group field-group--table';
    const input = document.createElement('table-field');
    // Slotted from the form's own tree so section cards can toggle the indicator.
    const tableLabel = document.createElement('span');
    tableLabel.slot = 'label';
    tableLabel.className = 'table-card-label';
    tableLabel.textContent = field.label;
    if (field.required) tableLabel.appendChild(requiredIndicator());
    input.appendChild(tableLabel);
    input.field = field;
    group.appendChild(input);
    return input;
}

/** Number input; grid position fields get their range and a hint. */
function renderNumber(field, group, {gridHint}) {
    const input = document.createElement('input');
    input.type = 'number';
    input.step = 'any';
    const fieldHintMeta = gridHint?.(field.key);
    if (fieldHintMeta) {
        input.step = '1';
        const hintText = fieldHintMeta.max && fieldHintMeta.max < GRID_MAX_COLUMNS && [GridKey.COLUMN, GridKey.WIDTH].includes(fieldHintMeta.baseKey)
            ? tpl('pageForm.gridHintLimited', {count: fieldHintMeta.max})
            : t(fieldHintMeta.hintKey);
        input.min = String(fieldHintMeta.min);
        if (fieldHintMeta.max != null) {
            input.max = String(fieldHintMeta.max);
            input.placeholder = `${fieldHintMeta.min}-${fieldHintMeta.max}`;
        } else {
            input.removeAttribute('max');
            input.placeholder = `${fieldHintMeta.min}+`;
        }
        input.title = hintText;

        const hint = document.createElement('small');
        hint.className = 'field-hint';
        hint.textContent = hintText;
        group.appendChild(hint);
    } else {
        input.placeholder = tpl('pageForm.inputPlaceholder', {label: field.label.toLowerCase()});
    }
    group.appendChild(input);
    return input;
}

function renderCheckbox(field, group, {bindLayoutToggle}) {
    const checkboxWrapper = document.createElement('label');
    checkboxWrapper.className = 'checkbox-field';
    const input = document.createElement('input');
    input.type = 'checkbox';
    bindLayoutToggle?.(input, field.key);
    checkboxWrapper.appendChild(input);
    group.appendChild(checkboxWrapper);
    return input;
}

function renderSelect(field, group) {
    const input = document.createElement('select');
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = t('pageForm.selectPlaceholder');
    input.appendChild(placeholder);
    for (const value of field.options || []) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        input.appendChild(option);
    }
    group.appendChild(input);
    return input;
}

function renderText(field, group) {
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = tpl('pageForm.inputPlaceholder', {label: field.label.toLowerCase()});
    group.appendChild(input);
    return input;
}

/**
 * Adds the field's help text as a muted paragraph under its label and describes the input with
 * it. Image cards keep it under their header (visible while collapsed) and describe the header
 * button with it too, since that button takes focus; table cards slot it under their header,
 * whose toggle the table field describes with it; checkbox rows put it after the switch so it
 * spans the row.
 */
function appendHelp(field, group, input) {
    const help = document.createElement('p');
    help.className = 'field-help';
    help.id = `help-${field.key}`;
    help.textContent = field.help;
    if (field.type === FieldType.TABLE) {
        help.slot = 'help';
        input.appendChild(help);
    } else if (field.type === FieldType.IMAGE) {
        const header = group.querySelector('.image-card-header');
        header.insertAdjacentElement('afterend', help);
        header.setAttribute('aria-describedby', help.id);
    } else if (field.type === FieldType.CHECKBOX) {
        group.appendChild(help);
    } else {
        group.querySelector(':scope > label[for]').insertAdjacentElement('afterend', help);
    }
    addDescribedBy(input, help.id);
}

/** Adds an id to an element's `aria-describedby`, after the ids it already holds. */
function addDescribedBy(element, id) {
    const ids = element.getAttribute('aria-describedby');
    element.setAttribute('aria-describedby', ids ? `${ids} ${id}` : id);
}

/** Refresh function of every rendered character counter, keyed by the counter element. */
const counterRefreshers = new WeakMap();

/**
 * Adds a live character counter under a text or textarea input, measured against the field's
 * `recommendedLength`. It counts the raw value length: leading and trailing spaces and line
 * breaks count too, as they do in the input. Past the recommendation the counter takes the
 * warning colour and adds a short note; it is a guide only and never blocks saving. Typing
 * updates it; after writing values programmatically, call {@link syncFieldCounters}.
 */
function appendCounter(field, input) {
    const counter = document.createElement('p');
    counter.className = 'field-counter';
    counter.id = `counter-${field.key}`;
    counter.setAttribute('aria-live', 'polite');
    const refresh = () => {
        const count = input.value.length;
        const over = count > field.recommendedLength;
        counter.textContent = tpl('pageForm.charCounter', {count, max: field.recommendedLength});
        counter.classList.toggle('is-over', over);
        if (over) {
            const note = document.createElement('span');
            note.className = 'field-counter-note';
            note.textContent = t('pageForm.charCounterOver');
            counter.append(', ', note);
        }
    };
    input.addEventListener('input', refresh);
    counterRefreshers.set(counter, refresh);
    input.insertAdjacentElement('afterend', counter);
    addDescribedBy(input, counter.id);
    refresh();
}

/**
 * Brings every character counter under a root up to date with its input's value. Needed after
 * values are written programmatically (a prefill), since that fires no `input` event.
 * @param {ParentNode} root
 */
export function syncFieldCounters(root) {
    root.querySelectorAll('.field-counter').forEach((counter) => counterRefreshers.get(counter)());
}

const RENDERERS = {
    [FieldType.TEXTAREA]: renderTextarea,
    [FieldType.IMAGE]: renderImage,
    [FieldType.DOCUMENT]: renderDocument,
    [FieldType.TABLE]: renderTable,
    [FieldType.NUMBER]: renderNumber,
    [FieldType.CHECKBOX]: renderCheckbox,
    [FieldType.SELECT]: renderSelect,
};

/**
 * Builds the `.field-group` of one template field: its label and the control for the field
 * type (a text input for any other type), named after the field key so the form submits it.
 * A field with `help` text shows it under its label; a text or textarea field with a
 * `recommendedLength` gets a live character counter under its input. The slug field also gets
 * its hint, the live URL preview pill and the address-change warning, unless the context asks
 * for a plain slug.
 *
 * @param {import('../../shell/types/user.types.js').TemplateField} field
 * @param {FieldRenderContext} context
 * @returns {{group: HTMLDivElement, input: HTMLElement}}
 */
export function renderFieldGroup(field, context) {
    const group = document.createElement('div');
    group.className = 'field-group';
    group.dataset.fieldKey = field.key;

    if (field.type !== FieldType.IMAGE && field.type !== FieldType.TABLE) {
        const label = document.createElement('label');
        label.setAttribute('for', field.key);
        label.textContent = field.label;
        if (field.required) label.appendChild(requiredIndicator());
        group.appendChild(label);
    }

    const render = Object.hasOwn(RENDERERS, field.type) ? RENDERERS[field.type] : renderText;
    const input = render(field, group, context);
    input.id = field.key;
    input.name = field.key;
    if (field.required) {
        input.required = true;
    }
    if (field.help) {
        appendHelp(field, group, input);
    }
    if (field.recommendedLength && isTextFieldType(field.type)) {
        appendCounter(field, input);
    }
    if (field.key === PageKey.SLUG && !context.plainSlug) {
        const hint = document.createElement('small');
        hint.className = 'field-hint';
        hint.textContent = t('pageForm.slugHint');
        group.appendChild(hint);
        group.appendChild(createSlugPreviewPill());
        group.appendChild(createSlugMoveWarning());
    }
    return {group, input};
}
